import React, { useState, useRef, useEffect } from 'react';
import './setting.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../../components/Sidebar';
import { UserProfileService } from '../../services/userProfile.service';
import { supabase } from '../../lib/supabase';
import CustomDatePicker from '../../components/CustomDatePicker';


const SettingsPage = () => {
  // Profile data state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: 'ja',
    occupation: '',
    industry: '',
    position_level: '',
    address: '',
    postal_code: '',
    birth_date: '',
    gender: 'male'
  });
  
  // UI state
  const [previewSrc, setPreviewSrc] = useState('/img/default-profile.png');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  
  const [zipFetching, setZipFetching] = useState(false);
  const [zipError, setZipError] = useState('');
  
  // Password change state
  const [passwordVisible, setPasswordVisible] = useState({ current: false, new: false, confirm: false });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const fileInputRef = useRef(null);

  const timezones = Intl.supportedValuesOf
    ? Intl.supportedValuesOf('timeZone')
    : ['Asia/Tokyo', 'Asia/Seoul', 'Asia/Singapore', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles'];

  // Load user profile data on component mount
  useEffect(() => {
    loadUserProfile();
  }, []);


  // 郵便番号→住所 自動補完（7桁で発火）
  const lookupAddressByZip = async (rawZip) => {
    const zipcode = (rawZip || '').replace(/[^0-9]/g, ''); // 数字のみ
    setZipError('');

    // 7桁揃ってなければ住所をクリアして終了
    if (zipcode.length !== 7) {
      setProfileData(prev => ({ ...prev, address: '' }));
      return;
    }

    try {
      setZipFetching(true);
      const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zipcode}`);
      const json = await res.json();

      if (json.status !== 200) throw new Error(json.message || '郵便番号検索に失敗しました。');
      const hit = (json.results && json.results[0]) || null;
      if (!hit) throw new Error('該当する住所が見つかりません。');

      const composed = `${hit.address1}${hit.address2}${hit.address3}`; // 都道府県 + 市区町村 + 町域

      // 住所はAPIの結果“だけ”を反映（手入力は不可なので上書き）
      setProfileData(prev => ({
        ...prev,
        address: composed,
        // 必要なら分割保持も
        prefecture: hit.address1,
        city: hit.address2,
        town: hit.address3,
      }));
    } catch (e) {
      console.error(e);
      setZipError(e.message || '郵便番号検索に失敗しました。');
      setProfileData(prev => ({ ...prev, address: '' })); // 失敗時は空に
    } finally {
      setZipFetching(false);
    }
  };



  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const profile = await UserProfileService.getCurrentUserProfile();
      
      // Map API response to form state
      setProfileData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        timezone: profile.timezone || 'Asia/Tokyo',
        language: profile.language || 'ja',
        occupation: profile.occupation || '',
        industry: profile.industry || '',
        position_level: profile.position_level || '',
        address: profile.address || '',
        birth_date: profile.birth_date || '',
        gender: profile.gender || 'male'
      });

      // Load avatar if exists
      if (profile.avatar_path) {
        const avatarUrl = await UserProfileService.getAvatarUrl();
        if (avatarUrl) {
          setPreviewSrc(avatarUrl);
        }
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setErrors({ general: 'プロフィールの読み込みに失敗しました。' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await uploadAvatar(file);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      await uploadAvatar(file);
    }
  };

  const uploadAvatar = async (file) => {
    try {
      setUploadingAvatar(true);
      setErrors(prev => ({ ...prev, avatar: '' }));

      // Show preview immediately
      const reader = new FileReader();
      reader.onload = (e) => setPreviewSrc(e.target.result);
      reader.readAsDataURL(file);

      // Upload to server
      const result = await UserProfileService.uploadAvatar(file);
      
      if (result.signed_url) {
        setPreviewSrc(result.signed_url);
      }
      
      setSuccessMessage('アバターが正常にアップロードされました。');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Avatar upload failed:', error);
      setErrors(prev => ({ ...prev, avatar: error.message || 'アバターのアップロードに失敗しました。' }));
      // Reset preview on error
      setPreviewSrc('/img/default-profile.png');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setErrors({});
      setSuccessMessage('');

      // Check required fields
      const requiredFieldErrors = {};
      if (!profileData.phone || profileData.phone.trim() === '') {
        requiredFieldErrors.phone = '電話番号を入力してください。';
      }
      
      if (Object.keys(requiredFieldErrors).length > 0) {
        setErrors(requiredFieldErrors);
        return;
      }

      // Validate profile data
      const validation = UserProfileService.validateProfileData(profileData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        return;
      }

      // Handle password change if provided
      if (newPassword) {
        if (!currentPassword) {
          setErrors(prev => ({ ...prev, current_password: 'パスワードを変更するためには既存パスワードを入力してください。' }));
          return;
        }
        if (newPassword !== confirmPassword) {
          setErrors(prev => ({ ...prev, confirm_password: 'パスワードが一致しません。' }));
          return;
        }

        // Update password using Supabase Auth
        const { error: passwordError } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (passwordError) {
          setErrors(prev => ({ ...prev, password: passwordError.message || 'パスワードの更新に失敗しました。' }));
          return;
        }
      }

      // Format and update profile data
      const formattedData = UserProfileService.formatProfileData(profileData);
      await UserProfileService.updateUserProfile(formattedData);

      setSuccessMessage('設定が正常に保存されました。');
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Save failed:', error);
      setErrors({ general: error.message || '設定の保存に失敗しました。' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="system-page">
        <div><Sidebar /></div>
        <main className="settings-content-wrapper">
          <div className="background-overlay"></div>
          <div className="settings-content">
            <div className="loading-message">ローディング中...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="system-page">
      <div><Sidebar /></div>
      <main className="settings-content-wrapper">
        <div className="background-overlay"></div>
        <div className="settings-content">
          {/* Success Message */}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}
          
          {/* General Error Message */}
          {errors.general && (
            <div className="error-message">{errors.general}</div>
          )}
          
          <form onSubmit={handleSubmit} id="settings-form">
            <div className="form-submit">
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={saving || uploadingAvatar}
              >
                {saving ? '保存中...' : '変更を保存する'}
              </button>
            </div>

            <div className="form-section">
              <h2>基本情報</h2>
              <div className="form-group profile-photo">
                <label>プロフィール写真</label>
                <div 
                  className={`circle-photo ${uploadingAvatar ? 'uploading' : ''}`}
                  onDragOver={(e) => e.preventDefault()} 
                  onDrop={handleDrop} 
                  onClick={() => !uploadingAvatar && fileInputRef.current.click()}
                >
                  <img id="preview" src={previewSrc} alt="" />
                  {previewSrc === '/img/default-profile.png' && !uploadingAvatar && 
                    <img src="/camera.png" alt="カメラアイコン" className="camera-icon" />
                  }
                  {uploadingAvatar && <div className="upload-spinner">アップロード中...</div>}
                  <input 
                    ref={fileInputRef} 
                    type="file" 
                    accept="image/jpeg,image/jpg,image/png" 
                    hidden 
                    onChange={handleFileChange} 
                    disabled={uploadingAvatar}
                  />
                </div>
                {errors.avatar && <p className="error-message">{errors.avatar}</p>}
              </div>
              <div className="form-group">
                <label>氏名</label>
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="氏名を入力"
                />
                {errors.name && <p className="error-message">{errors.name}</p>}
              </div>
              
              <div className="form-group">
                <label>生年月日</label>
                <CustomDatePicker
                  value={profileData.birth_date}
                  onChange={(val) => handleInputChange('birth_date', val)}
                />

                {errors.birth_date && <p className="error-message">{errors.birth_date}</p>}
              </div>
              
              <div className="form-group">
                <label>性別</label>
                <select 
                  value={profileData.gender} 
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                >
                  <option value="male">男性</option>
                  <option value="female">女性</option>
                  <option value="other">その他</option>
                  <option value="prefer_not_to_say">回答しない</option>
                </select>
                {errors.gender && <p className="error-message">{errors.gender}</p>}
              </div>
              
              <div className="form-group">
                <label>職業</label>
                <select
                  value={profileData.occupation}
                  onChange={(e) => handleInputChange('occupation', e.target.value)}
                >
                  <option value="">選択してください</option>
                  <option value="会社員">会社員</option>
                  <option value="公務員">公務員</option>
                  <option value="会社役員">会社役員</option>
                  <option value="自営業">自営業</option>
                  <option value="アルバイト・パート">アルバイト・パート</option>
                  <option value="主婦（主夫）">主婦（主夫）</option>
                  <option value="学生">学生</option>
                  <option value="その他">その他</option>
                </select>

                {/* 「その他」を選んだ時だけテキスト入力欄を表示 */}
                {profileData.occupation === 'その他' && (
                  <input
                    type="text"
                    value={profileData.otherOccupation || ''}
                    onChange={(e) => handleInputChange('otherOccupation', e.target.value)}
                    placeholder="その他の職業を入力してください"
                  />
                )}
              </div>

              
              <div className="form-group">
                <label>業界・分野</label>
                <select
                  value={profileData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                >
                  <option value="">選択してください</option>
                  <option value="IT・情報通信">IT・情報通信</option>
                  <option value="インターネット・Webサービス">インターネット・Webサービス</option>
                  <option value="通信・インフラ">通信・インフラ</option>
                  <option value="エネルギー・電力・ガス">エネルギー・電力・ガス</option>
                  <option value="エンタメ・レジャー・スポーツ">エンタメ・レジャー・スポーツ</option>
                  <option value="医療・福祉・介護">医療・福祉・介護</option>
                  <option value="飲食・宿泊・観光">飲食・宿泊・観光</option>
                  <option value="運輸・物流・倉庫">運輸・物流・倉庫</option>
                  <option value="教育・研修・学習支援">教育・研修・学習支援</option>
                  <option value="金融・保険・証券">金融・保険・証券</option>
                  <option value="建設・不動産・住宅">建設・不動産・住宅</option>
                  <option value="小売・卸売・販売">小売・卸売・販売</option>
                  <option value="広告・マーケティング・デザイン">広告・マーケティング・デザイン</option>
                  <option value="公共・団体・官公庁">公共・団体・官公庁</option>
                  <option value="製造業（食品・飲料）">製造業（食品・飲料）</option>
                  <option value="製造業（化学・素材）">製造業（化学・素材）</option>
                  <option value="製造業（機械・電気・電子）">製造業（機械・電気・電子）</option>
                  <option value="製造業（自動車・輸送機器）">製造業（自動車・輸送機器）</option>
                  <option value="製造業（その他）">製造業（その他）</option>
                  <option value="農林水産・一次産業">農林水産・一次産業</option>
                  <option value="美容・理容・ファッション">美容・理容・ファッション</option>
                  <option value="貿易・商社">貿易・商社</option>
                  <option value="人材・派遣・アウトソーシング">人材・派遣・アウトソーシング</option>
                  <option value="コンサルティング・士業">コンサルティング・士業</option>
                  <option value="メディア・出版・印刷">メディア・出版・印刷</option>
                  <option value="研究・開発">研究・開発</option>
                  <option value="サービス業（その他）">サービス業（その他）</option>
                  <option value="学生">学生</option>
                  <option value="その他">その他</option>
                </select>

                {/* 「その他」を選んだ時だけテキスト入力欄を表示 */}
                {profileData.industry === 'その他' && (
                  <input
                    type="text"
                    value={profileData.otherIndustry || ''}
                    onChange={(e) => handleInputChange('otherIndustry', e.target.value)}
                    placeholder="その他の業界・分野を入力してください"
                  />
                )}
              </div>


              
              <div className="form-group">
                <label>役職・ポジション</label>
                <select
                  value={profileData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                >
                  <option value="">選択してください</option>
                  <option value="経営者・役員">経営者・役員</option>
                  <option value="管理職（部長・課長など）">管理職（部長・課長など）</option>
                  <option value="一般社員・スタッフ">一般社員・スタッフ</option>
                  <option value="契約社員・アルバイト">契約社員・アルバイト</option>
                  <option value="自営業・フリーランス">自営業・フリーランス</option>
                  <option value="学生">学生</option>
                  <option value="その他">その他</option>
                </select>

                {/* 「その他」を選んだ時だけ表示 */}
                {profileData.position === 'その他' && (
                  <input
                    type="text"
                    value={profileData.otherPosition || ''}
                    onChange={(e) => handleInputChange('otherPosition', e.target.value)}
                    placeholder="その他の役職・ポジションを入力してください"
                  />
                )}
              </div>

              
              <div className="form-group">
                <label>言語</label>
                <select 
                  value={profileData.language} 
                  onChange={(e) => handleInputChange('language', e.target.value)}
                >
                  <option value="ja">日本語</option>
                  <option value="en">英語</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>タイムゾーン</label>
                <select 
                  value={profileData.timezone} 
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                >
                  {timezones.map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
                </select>
              </div>
            </div>

            <div className="form-section">
              <h2>連絡先情報</h2>
                
              
              <div className="form-group">
                <label>電話番号</label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="例: 09012345678"
                  required
                />
                {errors.phone && <p className="error-message">{errors.phone}</p>}
                <small className="form-help">ハイフンなしの形式で入力してください。(電話番号入力は必須です。)</small>
              </div>


              <div className="form-group">
                <label>メールアドレス</label>
                <input 
                  type="email" 
                  value={profileData.email} 
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="メールアドレスを入力"
                />
                {errors.email && <p className="error-message">{errors.email}</p>}
                {/* <small className="form-help">メールアドレスを変更した場合、確認メールが送信されます。</small> */}
              </div>

              
              <div className="form-group">
                <label>郵便番号</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={8} // 123-4567もOK
                    placeholder="例: 123-4567"
                    value={profileData.postal_code}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleInputChange('postal_code', v);
                      const digits = v.replace(/[^0-9]/g, '');
                      if (digits.length === 7) lookupAddressByZip(v); // 7桁で自動検索
                      if (digits.length < 7) setProfileData(prev => ({ ...prev, address: '' })); // 未満なら住所クリア
                    }}
                    onBlur={(e) => lookupAddressByZip(e.target.value)} // フォーカスアウト時も保険
                  />
                  {zipFetching && <small>住所検索中...</small>}
                </div>
                {zipError && <p className="error-message">{zipError}</p>}
                <small className="form-help">7桁入力で自動検索します（ハイフン可）。</small>
              </div>



              
              <div className="form-group">
                <label>住所</label>

                {/* 表示専用ボックス（スクリーンリーダーに更新を通知） */}
                <div className="address-box" role="status" aria-live="polite">
                  {profileData.address
                    ? profileData.address
                    : '郵便番号を入力すると住所が自動表示されます'}
                </div>

                {/* 送信用（フォームとして値を送る必要がある場合） */}
                <input type="hidden" name="address" value={profileData.address} />

                {/* 入力欄は廃止のため onChange 等は不要 */}
              </div>
            </div>

            <div className="form-section">
              <h3>パスワードを変更する</h3>
              <small className="form-help">パスワードを変更しない場合は空欄のままにしてください。</small>
              
              {['current', 'new', 'confirm'].map((field) => (
                <div key={field} className="form-group password-group">
                  <label>
                    {field === 'current' ? '現在のパスワード' : field === 'new' ? '新しいパスワード' : 'パスワード確認'}
                  </label>
                  <div className="password-wrapper">
                    <input
                      type={passwordVisible[field] ? 'text' : 'password'}
                      id={field === 'new' ? 'new-password' : field === 'confirm' ? 'confirm-password' : 'current-password'}
                      value={field === 'current' ? currentPassword : field === 'new' ? newPassword : confirmPassword}
                      onChange={(e) => {
                        if (field === 'current') setCurrentPassword(e.target.value);
                        if (field === 'new') setNewPassword(e.target.value);
                        if (field === 'confirm') setConfirmPassword(e.target.value);
                        // Clear related errors
                        if (errors[field + '_password']) {
                          setErrors(prev => ({ ...prev, [field + '_password']: '' }));
                        }
                        if (errors.password) {
                          setErrors(prev => ({ ...prev, password: '' }));
                        }
                      }}
                      placeholder={field === 'current' ? '現在のパスワードを入力' : field === 'new' ? '新しいパスワードを入力' : '新しいパスワードを再入力'}
                    />
                    <button 
                      type="button" 
                      className="eye-btn" 
                      onClick={() => setPasswordVisible({ ...passwordVisible, [field]: !passwordVisible[field] })}
                    >
                      <FontAwesomeIcon icon={passwordVisible[field] ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {errors[field + '_password'] && <p className="error-message">{errors[field + '_password']}</p>}
                  {errors.password && field === 'new' && <p className="error-message">{errors.password}</p>}
                </div>
              ))}
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;

